import { LegalPolicyFooter } from "@/components/legal/LegalPolicyLinks";
import {
  getReturnButtonLabel,
  getReturnToUrl,
  getSafeReturnTo,
} from "@/lib/navigation/publicReturnTo";
import { SOKO_GRID_EMAIL_URL } from "@/src/config/contact";
import { router, useLocalSearchParams } from "expo-router";
import React, { useCallback, useMemo, useState } from "react";
import {
  KeyboardAvoidingView,
  Linking,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

const ISSUE_TYPES = [
  "Page or button not working",
  "Search issue",
  "Saved businesses issue",
  "Wrong Soko Grid information",
  "Login/account issue",
  "Payment/subscription issue",
  "Other",
];

const USER_TYPES = ["Public user/customer", "Business owner"] as const;
const PAGE_CONFIRMATION_OPTIONS = [
  "Yes, this page has the issue",
  "No, the issue is somewhere else",
] as const;

type UserType = (typeof USER_TYPES)[number];
type PageConfirmation = (typeof PAGE_CONFIRMATION_OPTIONS)[number];

type IssueEmailFields = {
  userType: UserType;
  pageConfirmation: PageConfirmation;
  returnTo: string;
  issueType: string;
  details: string;
  ownerName: string;
  businessName: string;
  ownerGmail: string;
  ownerPhone: string;
};

function cleanText(value: unknown) {
  return String(value ?? "").trim();
}

function getCurrentReportPageUrl() {
  if (typeof window !== "undefined" && window.location?.href) {
    return window.location.href;
  }

  if (typeof document !== "undefined" && document.location?.href) {
    return document.location.href;
  }

  return "Not available";
}

function formatOptional(value: string) {
  return cleanText(value) || "Not provided";
}

function buildIssueMailtoUrl(fields: IssueEmailFields) {
  const ownerLines =
    fields.userType === "Business owner"
      ? [
          `Owner name: ${formatOptional(fields.ownerName)}`,
          `Business name: ${formatOptional(fields.businessName)}`,
          `Gmail used to sign in: ${formatOptional(fields.ownerGmail)}`,
          `Contact phone number: ${formatOptional(fields.ownerPhone)}`,
        ]
      : [];

  const body = [
    `User type: ${fields.userType}`,
    `Is this issue about the page you came from?: ${fields.pageConfirmation}`,
    `Page came from: ${getReturnToUrl(fields.returnTo)}`,
    `Issue type: ${fields.issueType}`,
    ...ownerLines,
    `Optional details: ${formatOptional(fields.details)}`,
    `Current report page URL: ${getCurrentReportPageUrl()}`,
    `Date/time: ${new Date().toISOString()}`,
  ].join("\n");

  return `${SOKO_GRID_EMAIL_URL}?subject=${encodeURIComponent(
    "Soko Grid issue report"
  )}&body=${encodeURIComponent(body)}`;
}

function goToReturnPath(returnTo: string) {
  if (returnTo) {
    router.push(returnTo as any);
    return;
  }

  router.push("/" as any);
}

function OptionButton({
  label,
  selected,
  onPress,
}: {
  label: string;
  selected: boolean;
  onPress: () => void;
}) {
  return (
    <Pressable
      accessibilityRole="radio"
      accessibilityState={{ checked: selected }}
      onPress={onPress}
      style={({ pressed }) => [
        styles.option,
        selected ? styles.optionSelected : null,
        pressed ? styles.pressed : null,
      ]}
    >
      <View
        style={[styles.radioOuter, selected ? styles.radioOuterSelected : null]}
      >
        {selected ? <View style={styles.radioInner} /> : null}
      </View>

      <Text style={[styles.optionText, selected ? styles.optionTextSelected : null]}>
        {label}
      </Text>
    </Pressable>
  );
}

export default function ReportIssuePage() {
  const params = useLocalSearchParams<{ returnTo?: string | string[] }>();
  const returnTo = useMemo(
    () => getSafeReturnTo(params.returnTo),
    [params.returnTo]
  );

  const [userType, setUserType] = useState<UserType>(USER_TYPES[0]);
  const [pageConfirmation, setPageConfirmation] =
    useState<PageConfirmation>(PAGE_CONFIRMATION_OPTIONS[0]);
  const [selectedIssueType, setSelectedIssueType] = useState(ISSUE_TYPES[0]);
  const [details, setDetails] = useState("");
  const [ownerName, setOwnerName] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [ownerGmail, setOwnerGmail] = useState("");
  const [ownerPhone, setOwnerPhone] = useState("");

  const handleBack = useCallback(() => {
    goToReturnPath(returnTo);
  }, [returnTo]);

  const handleSubmit = useCallback(async () => {
    const mailtoUrl = buildIssueMailtoUrl({
      userType,
      pageConfirmation,
      returnTo,
      issueType: selectedIssueType,
      details,
      ownerName,
      businessName,
      ownerGmail,
      ownerPhone,
    });

    if (Platform.OS === "web" && typeof window !== "undefined") {
      window.location.href = mailtoUrl;
      return;
    }

    await Linking.openURL(mailtoUrl);
  }, [
    businessName,
    details,
    ownerGmail,
    ownerName,
    ownerPhone,
    pageConfirmation,
    returnTo,
    selectedIssueType,
    userType,
  ]);

  const showOwnerFields = userType === "Business owner";

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === "ios" ? "padding" : undefined}
      style={styles.keyboardView}
    >
      <ScrollView
        style={styles.screen}
        contentContainerStyle={styles.content}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator
      >
        <SafeAreaView style={styles.safeAreaContent}>
          <View style={styles.container}>
            <Text style={styles.title}>Report an Issue</Text>
            <Text style={styles.intro}>
              Use this page to report a problem with the Soko Grid website,
              menu, buttons, search, saved businesses, or general platform
              experience.
            </Text>

            <View style={styles.formCard}>
              <Text style={styles.label}>User type</Text>
              <View style={styles.optionGroup}>
                {USER_TYPES.map((type) => (
                  <OptionButton
                    key={type}
                    label={type}
                    selected={userType === type}
                    onPress={() => setUserType(type)}
                  />
                ))}
              </View>

              <Text style={styles.label}>
                Is this issue about the page you came from?
              </Text>
              <View style={styles.optionGroup}>
                {PAGE_CONFIRMATION_OPTIONS.map((option) => (
                  <OptionButton
                    key={option}
                    label={option}
                    selected={pageConfirmation === option}
                    onPress={() => setPageConfirmation(option)}
                  />
                ))}
              </View>

              {showOwnerFields ? (
                <View style={styles.ownerFields}>
                  <Text style={styles.label}>Business owner details</Text>

                  <TextInput
                    onChangeText={setOwnerName}
                    placeholder="Owner name"
                    placeholderTextColor="#94a3b8"
                    style={styles.input}
                    value={ownerName}
                  />

                  <TextInput
                    onChangeText={setBusinessName}
                    placeholder="Business name"
                    placeholderTextColor="#94a3b8"
                    style={styles.input}
                    value={businessName}
                  />

                  <TextInput
                    autoCapitalize="none"
                    keyboardType="email-address"
                    onChangeText={setOwnerGmail}
                    placeholder="Gmail used to sign in"
                    placeholderTextColor="#94a3b8"
                    style={styles.input}
                    value={ownerGmail}
                  />

                  <TextInput
                    keyboardType="phone-pad"
                    onChangeText={setOwnerPhone}
                    placeholder="Contact phone number"
                    placeholderTextColor="#94a3b8"
                    style={styles.input}
                    value={ownerPhone}
                  />
                </View>
              ) : null}

              <Text style={styles.label}>Issue type</Text>
              <View style={styles.optionGroup}>
                {ISSUE_TYPES.map((issueType) => (
                  <OptionButton
                    key={issueType}
                    label={issueType}
                    selected={selectedIssueType === issueType}
                    onPress={() => setSelectedIssueType(issueType)}
                  />
                ))}
              </View>

              <Text style={styles.label}>Optional details</Text>
              <TextInput
                multiline
                onChangeText={setDetails}
                placeholder={"Add more details\u2026"}
                placeholderTextColor="#94a3b8"
                style={styles.detailsInput}
                textAlignVertical="top"
                value={details}
              />
            </View>

            <View style={styles.actions}>
              <Pressable
                onPress={handleSubmit}
                style={({ pressed }) => [
                  styles.primaryButton,
                  pressed ? styles.pressed : null,
                ]}
              >
                <Text style={styles.primaryButtonText}>
                  Submit Issue Report
                </Text>
              </Pressable>

              <Pressable
                onPress={handleBack}
                style={({ pressed }) => [
                  styles.secondaryButton,
                  pressed ? styles.pressed : null,
                ]}
              >
                <Text style={styles.secondaryButtonText}>
                  {getReturnButtonLabel(returnTo)}
                </Text>
              </Pressable>
            </View>

            <LegalPolicyFooter
              returnTo="/report-issue"
              style={styles.legalLinks}
            />
          </View>
        </SafeAreaView>
      </ScrollView>
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  keyboardView: {
    flex: 1,
    backgroundColor: "#f8fafc",
  },
  screen: {
    flex: 1,
    backgroundColor: "#f8fafc",
  },
  content: {
    flexGrow: 1,
    width: "100%",
    paddingHorizontal: 20,
    paddingTop: 24,
    paddingBottom: 72,
  },
  safeAreaContent: {
    width: "100%",
  },
  container: {
    width: "100%",
    maxWidth: 760,
    alignSelf: "center",
  },
  title: {
    fontSize: 30,
    lineHeight: 36,
    fontWeight: "900",
    color: "#111827",
  },
  intro: {
    marginTop: 10,
    maxWidth: 640,
    fontSize: 16,
    lineHeight: 24,
    fontWeight: "700",
    color: "#475569",
  },
  formCard: {
    marginTop: 22,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#e5e7eb",
    backgroundColor: "#ffffff",
    padding: 18,
  },
  label: {
    fontSize: 12,
    lineHeight: 16,
    fontWeight: "900",
    color: "#334155",
    marginBottom: 8,
    textTransform: "uppercase",
  },
  optionGroup: {
    marginBottom: 14,
  },
  option: {
    minHeight: 42,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#e2e8f0",
    backgroundColor: "#ffffff",
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 12,
    marginBottom: 8,
  },
  optionSelected: {
    borderColor: "#1f8f5a",
    backgroundColor: "#ecfdf5",
  },
  radioOuter: {
    width: 18,
    height: 18,
    borderRadius: 9,
    borderWidth: 2,
    borderColor: "#cbd5e1",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 10,
  },
  radioOuterSelected: {
    borderColor: "#1f8f5a",
  },
  radioInner: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: "#1f8f5a",
  },
  optionText: {
    flex: 1,
    minWidth: 0,
    fontSize: 14,
    lineHeight: 19,
    fontWeight: "800",
    color: "#334155",
  },
  optionTextSelected: {
    color: "#166534",
  },
  ownerFields: {
    marginBottom: 14,
  },
  input: {
    minHeight: 44,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#cbd5e1",
    backgroundColor: "#f8fafc",
    color: "#111827",
    fontSize: 14,
    lineHeight: 20,
    fontWeight: "700",
    paddingHorizontal: 12,
    paddingVertical: 10,
    marginBottom: 10,
  },
  detailsInput: {
    minHeight: 112,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#cbd5e1",
    backgroundColor: "#f8fafc",
    color: "#111827",
    fontSize: 14,
    lineHeight: 20,
    fontWeight: "700",
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  actions: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 10,
    marginTop: 20,
  },
  legalLinks: {
    marginTop: 18,
  },
  primaryButton: {
    minHeight: 46,
    borderRadius: 8,
    backgroundColor: "#111827",
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 18,
  },
  primaryButtonText: {
    fontSize: 14,
    fontWeight: "900",
    color: "#ffffff",
  },
  secondaryButton: {
    minHeight: 46,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#d1d5db",
    backgroundColor: "#ffffff",
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 18,
  },
  secondaryButtonText: {
    fontSize: 14,
    fontWeight: "900",
    color: "#111827",
  },
  pressed: {
    opacity: 0.82,
  },
});
